import {
  Select,
  MenuItem,
  Box,
  Typography,
  FormControl,
  FormHelperText,
  Skeleton,
  Alert,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import type { Control, FieldError } from 'react-hook-form';
import type { Location } from '../types';
import FieldLabel from './FieldLabel';

interface LocationSelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  error?: FieldError;
  locations: Location[];
  loading: boolean;
  loadError: string | null;
  /** Feature 043 — practice settings can make Location optional. Defaults to true. */
  required?: boolean;
}

export default function LocationSelect({
  control,
  error,
  locations,
  loading,
  loadError,
  required = true,
}: LocationSelectProps) {
  return (
    <FormControl fullWidth error={!!error || !!loadError}>
      <FieldLabel required={required}>Location</FieldLabel>
      {loading ? (
        <Skeleton height={40} />
      ) : loadError ? (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {loadError}
        </Alert>
      ) : (
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              displayEmpty
              disabled={locations.length === 0}
              size="small"
              fullWidth
              error={!!error}
              aria-label="location"
              // Open the list as a dropdown directly below the trigger with a
              // small 2px gap (mt: 0.25). Cap it to ~3 locations then scroll —
              // each option is a rich, variable-height row (name + optional
              // address + NPI ≈ 84px for a full 3-line item), so this is a
              // height cap (≈3 × 84px + list padding), not an exact count.
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: { sx: { maxHeight: 260, mt: 0.25 } },
              }}
              // MUI `<Select>` adds a `min-height: 1.4375em` on the inner
              // `.MuiSelect-select`, which overflows the themed 36px small-input
              // height by ~1px — making this dropdown sit 1px taller than the
              // native selects/text fields. Clear it so the rich value content
              // fits within the 36px control and aligns with its siblings.
              sx={{ '& .MuiSelect-select': { minHeight: 'unset' } }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Choose Location
                    </Typography>
                  );
                }
                const loc = locations.find((l) => l.id === selected);
                return (
                  <Typography variant="body2" color="text.primary">
                    {loc ? loc.name : ''}
                  </Typography>
                );
              }}
            >
              {locations.map((loc) => (
                <MenuItem
                  key={loc.id}
                  value={loc.id}
                  sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1.5 }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {loc.name}
                    </Typography>
                    {loc.address && (
                      <Typography variant="body2" color="text.secondary">
                        {loc.address}
                      </Typography>
                    )}
                    {loc.npi && (
                      <Typography variant="body2" color="text.secondary">
                        NPI: {loc.npi}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        />
      )}
      {locations.length === 0 && !loading && !loadError && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No locations available.
        </Typography>
      )}
      {error && (
        <FormHelperText>{error.message}</FormHelperText>
      )}
    </FormControl>
  );
}
